import React from 'react';
import { useLanguage } from '../context/LanguageContext';

// Standard bus: 2+2 layout, ~56 seats
const STANDARD_ROWS = [
  [null, null, 4, 3],
  [5, 6, 8, 7],
  [9, 10, 12, 11],
  [13, 14, 16, 15],
  [17, 18, 20, 19],
  [21, 22, 24, 23],
  [25, 26, null, null],
  [27, 28, 30, 29],
  [33, null, 32, 31],
  [36, null, 34, null],
  [39, null, 38, 37],
  [42, null, 40, 41],
  [45, null, 44, 43],
];
export const STANDARD_RELAX = new Set([4, 3, 5, 6, 8, 7, 9, 10, 12, 11]);

// Lounge bus: 2+1 layout, ~32 seats, wider seats
const LOUNGE_ROWS = [
  [null, null, 3],
  [4, 5, 6],
  [7, 8, 9],
  [10, 11, 12],
  [13, 14, 15],
  [16, 17, null],
  [18, 19, null],
  [20, 21, 22],
  [23, 24, 25],
  [26, 27, 28],
  [29, 30, 31],
  [32, 33, 34],
];
export const LOUNGE_RELAX = new Set([3, 4, 5, 6, 7, 8, 9, 10, 11, 12]);

const BusSeatMap = ({ seatAssignments, occupiedSeats, activePassengerId, onSeatClick, busType = 'standard' }) => {
  const { t } = useLanguage();
  const isLounge = busType === 'lounge';
  const BUS_ROWS = isLounge ? LOUNGE_ROWS : STANDARD_ROWS;
  const RELAX_SEATS = isLounge ? LOUNGE_RELAX : STANDARD_RELAX;

  const seatToPassenger = {};
  Object.entries(seatAssignments).forEach(([pId, seatNum]) => {
    if (seatNum) seatToPassenger[seatNum] = parseInt(pId);
  });

  const getSeatState = (seatNum) => {
    if (seatToPassenger[seatNum] !== undefined) return 'assigned';
    if (occupiedSeats.has(seatNum)) return 'occupied';
    if (RELAX_SEATS.has(seatNum)) return 'relax';
    return 'standard';
  };

  const seatSize = isLounge ? 'w-12 h-12' : 'w-10 h-10';
  const emptySize = isLounge ? 'w-12 h-12' : 'w-10 h-10';

  const getSeatStyle = (seatNum) => {
    const state = getSeatState(seatNum);
    const base = `${seatSize} rounded-md text-xs font-bold flex items-center justify-center cursor-pointer transition-all select-none border-2`;

    if (state === 'assigned') return `${base} bg-[#c30909] text-white border-[#c30909]`;
    if (state === 'occupied') return `${base} bg-gray-200 text-gray-400 border-gray-200 cursor-not-allowed`;
    if (state === 'relax') return `${base} bg-[#f5f0e0] text-[#484c55] border-[#d4c9a8] hover:border-[#c30909] hover:bg-[#fef8e8]`;
    return `${base} bg-[#e8f5e9] text-[#484c55] border-[#4caf50] hover:border-[#c30909] hover:bg-[#f1faf1]`;
  };

  const handleClick = (seatNum) => {
    if (getSeatState(seatNum) === 'occupied') return;
    onSeatClick(seatNum);
  };

  const getSeatLabel = (seatNum) => {
    const pId = seatToPassenger[seatNum];
    if (pId !== undefined) return `P${pId}`;
    return seatNum;
  };

  const renderSeat = (seatNum) => {
    if (seatNum === null) return <div className={emptySize} />;
    return (
      <button
        className={getSeatStyle(seatNum)}
        onClick={() => handleClick(seatNum)}
        title={`${t('seat.seatLabel')} ${seatNum}`}
      >
        {getSeatLabel(seatNum)}
      </button>
    );
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4 inline-block">
      {/* Legend */}
      <div className="flex items-center gap-4 mb-4 text-xs flex-wrap">
        <div className="flex items-center gap-1.5">
          <div className="w-4 h-4 rounded border-2 border-[#4caf50] bg-[#e8f5e9]" />
          <span>{t('seat.standard')}</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-4 h-4 rounded border-2 border-[#d4c9a8] bg-[#f5f0e0]" />
          <span>{t('seat.relax')}</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-4 h-4 rounded border-2 border-[#c30909] bg-[#c30909]" />
          <span>{t('seat.selected')}</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-4 h-4 rounded border-2 border-gray-200 bg-gray-200" />
          <span>{t('seat.occupied')}</span>
        </div>
      </div>

      {/* Driver icon */}
      <div className="flex justify-center mb-3">
        <div className="w-10 h-10 rounded-full border-2 border-gray-300 flex items-center justify-center">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#999" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10"/>
            <path d="M8 14s1.5 2 4 2 4-2 4-2"/>
            <line x1="9" y1="9" x2="9.01" y2="9"/>
            <line x1="15" y1="9" x2="15.01" y2="9"/>
          </svg>
        </div>
      </div>

      {/* Bus body with red side borders */}
      <div className="relative">
        {/* Red side lines */}
        <div className="absolute left-0 top-4 bottom-4 w-1 bg-[#c30909] rounded-full" />
        <div className="absolute right-0 top-4 bottom-4 w-1 bg-[#c30909] rounded-full" />

        <div className="border-2 border-gray-200 rounded-2xl p-3 pt-4 mx-1">
          {BUS_ROWS.map((row, rowIdx) => (
            <div key={rowIdx} className="flex items-center justify-center gap-1 mb-1">
              {isLounge ? (
                <>
                  {/* Lounge: 2 left + aisle + 1 right */}
                  <div className="flex gap-1">
                    {renderSeat(row[0])}
                    {renderSeat(row[1])}
                  </div>
                  <div className="w-8" />
                  <div className="flex gap-1">
                    {renderSeat(row[2])}
                  </div>
                </>
              ) : (
                <>
                  {/* Standard: 2 left + aisle + 2 right */}
                  <div className="flex gap-1">
                    {renderSeat(row[0])}
                    {renderSeat(row[1])}
                  </div>
                  <div className="w-6" />
                  <div className="flex gap-1">
                    {renderSeat(row[2])}
                    {renderSeat(row[3])}
                  </div>
                </>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Bus type label */}
      <div className="text-center mt-3">
        <span className={`text-[10px] font-bold uppercase tracking-wider px-3 py-1 rounded-full ${
          isLounge ? 'bg-[#b8960c]/20 text-[#8a7000]' : 'bg-[#c30909]/10 text-[#c30909]'
        }`}>
          {isLounge ? 'LUX Express Lounge' : 'LUX Express'}
        </span>
      </div>
    </div>
  );
};

export default BusSeatMap;
